'use client';

import { c as _c } from "react/compiler-runtime";
import { BackgroundLogo } from '@/components/background-logo';
import { AppErrorFallback } from '@/components/error-boundary/app-error-fallback';
import { FooterClientWrapper } from '@/components/footer/footer-client-wrapper';
import { DynamicAppTitleProvider } from '@/components/header/dynamic-app-title-name';
import { HeaderActionsProvider } from '@/components/header/header-actions';
import { HeaderClientWrapper } from '@/components/header/header-client-wrapper';
import { HideHeaderProvider } from '@/components/header/hide-header-context';
import { NativePushProvider } from '@/components/native-push-provider';
import { OfflineBanner } from '@/components/offline-banner';
import { useHideBackgroundLogo } from '@/components/ui/hide-background-logo-context';
import { ClientProviders } from '@/context/client-providers';
import { OfflineQueueSync } from '@/features/chat/hooks/use-offline-queue-processor';
import { PostHogProvider } from '@/providers/post-hog-provider';
import { TRPCProvider } from '@/trpc/client';
import type { Locale, NavigationMode } from '@/types/types';
import { cn } from '@/utils/tailwindcss-override';
import type { ReactNode } from 'react';
import React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
interface AppShellProperties {
  children: ReactNode;
  header: ReactNode;
  footer?: ReactNode;
  inAppDesign: boolean;
  locale: Locale;
  swUrl?: string;
  navigationMode?: NavigationMode;
}

/**
 * Unified App Shell component that provides all necessary providers
 * and layout structure for both online and offline versions.
 */
export const AppShell: React.FC<AppShellProperties> = t0 => {
  const $ = _c(26);
  const {
    children,
    header,
    footer,
    inAppDesign,
    locale,
    navigationMode: t1
  } = t0;
  const navigationMode = t1 === undefined ? "side-nav" : t1;
  const {
    hideBackgroundLogo
  } = useHideBackgroundLogo();
  const isTopNavMode = navigationMode === "top-nav";
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <OfflineQueueSync />;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== header) {
    t3 = <HeaderClientWrapper>{header}</HeaderClientWrapper>;
    $[1] = header;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== hideBackgroundLogo || $[4] !== inAppDesign || $[5] !== isTopNavMode) {
    t4 = !hideBackgroundLogo && !inAppDesign && <div className={cn("absolute top-0 z-[-999] h-screen w-full p-[56px]", isTopNavMode ? "lg:hidden" : "xl:pl-[480px]")}><BackgroundLogo className="max-h-[60vh]" /></div>;
    $[3] = hideBackgroundLogo;
    $[4] = inAppDesign;
    $[5] = isTopNavMode;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const t5 = isTopNavMode ? "xl:[--app-content-top:calc(4rem+2px+var(--preview-banner-height))] xl:[--app-header-height:calc(4rem+var(--preview-banner-height))]" : "xl:ml-[480px] xl:[--app-content-left:480px]";
  let t6;
  if ($[7] !== t5) {
    t6 = cn("wco-content-wrapper mt-[calc(62px+var(--preview-banner-height))] h-[calc(100dvh-62px-var(--preview-banner-height))] [--app-content-left:0px] [--app-content-top:calc(62px+var(--preview-banner-height))] [--app-header-height:calc(60px+var(--preview-banner-height))]", t5);
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== locale) {
    t7 = <OfflineBanner locale={locale} className="sticky top-(--app-header-height) z-40 pl-28 xl:pl-4" />;
    $[9] = locale;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== children) {
    t8 = <div className="flex-1"><ErrorBoundary FallbackComponent={AppErrorFallback}>{children}</ErrorBoundary></div>;
    $[11] = children;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== footer) {
    t9 = footer !== null && footer !== undefined && <FooterClientWrapper>{footer}</FooterClientWrapper>;
    $[13] = footer;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t7 || $[16] !== t8 || $[17] !== t9) {
    t10 = <main className="flex min-h-full flex-col justify-between">{t7}{t8}{t9}</main>;
    $[15] = t7;
    $[16] = t8;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t10 || $[20] !== t6) {
    t11 = <div className={t6}>{t10}</div>;
    $[19] = t10;
    $[20] = t6;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t11 || $[23] !== t3 || $[24] !== t4) {
    t12 = <PostHogProvider><TRPCProvider><ClientProviders>{t2}<NativePushProvider><HideHeaderProvider><DynamicAppTitleProvider><HeaderActionsProvider>{t3}{t4}{t11}</HeaderActionsProvider></DynamicAppTitleProvider></HideHeaderProvider></NativePushProvider></ClientProviders></TRPCProvider></PostHogProvider>;
    $[22] = t11;
    $[23] = t3;
    $[24] = t4;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  return t12;
};